'use client'
import { useTranslate } from '../../hooks'
import { type FormSchema, useAppForm } from '@repo/ui/hooks'

import { useOrderBoxSchema } from '../../schemas'
import {
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Typography,
  Row,
} from '@repo/ui/components'
import {
  AdvancedTradeBoxAmountInput,
  AdvancedTradeBoxPriceInput,
  AdvancedTradeBoxTotalInput,
  AdvancedTradeBoxSubmitButton,
  AdvancedTradeBoxBalanceSlider,
  AdvancedTradeBoxToggle,
  AdvancedTradeBoxCheckbox,
  AdvancedTradeBoxMobileCheckbox,
  AdvancedTradeBoxFeeDisplay,
} from './'
import type {
  AdvancedTradeBoxFormValues,
  AdvancedTradeBoxOrderType,
  AdvancedTradeBoxTab,
} from './advanced-trade-box.types'
import { useRouter } from '../../i18n/navigation'
import { useState, useEffect } from 'react'
import { useIsDesktop } from '@repo/ui/hooks'
import { twMerge } from 'tailwind-merge'

interface AdvancedTradeBoxFormProps {
  tab: AdvancedTradeBoxTab
  orderType: AdvancedTradeBoxOrderType
  availableAmount: number
  availableBalance: number
  onSubmit?: (data: AdvancedTradeBoxFormValues) => void
  inputCurrency: 'IRR' | 'TMN'
  outputCurrency: 'IRR' | 'TMN'
  instantPrice: number
  isLoading: boolean
  targetScale?: number
}

export const AdvancedTradeBoxForm = (props: AdvancedTradeBoxFormProps) => {
  const {
    tab,
    orderType,
    availableAmount,
    availableBalance,
    onSubmit,
    inputCurrency,
    outputCurrency,
    instantPrice,
    isLoading,
    targetScale = 1,
  } = props
  const { t } = useTranslate()
  const { push } = useRouter()
  const [toggleValue, setToggleValue] = useState<'total' | 'amount'>('total')
  const [takeProfit, setTakeProfit] = useState(false)
  const [stopLoss, setStopLoss] = useState(false)
  const isDesktop = useIsDesktop()
  const { schema } = useOrderBoxSchema({
    tab,
    priceType: orderType === 'limit' ? 'limit' : 'market',
    availableBalance,
    instantPrice,
    inputCurrency,
    outputCurrency,
    maxTokenAmount: availableAmount,
    targetScale,
  })

  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      amount: 0,
      price: orderType === 'limit' ? instantPrice : 0,
      activationPrice: orderType === 'stop-limit' ? instantPrice : 0,
      total: 0,
      range: 0,
      takeProfit: 0,
      stopLoss: 0,
    },
  })

  // Set initial total for limit orders
  useEffect(() => {
    if (orderType === 'limit' || orderType === 'stop-limit') {
      const price = form.watch('price') || instantPrice
      const amount = form.watch('amount') || 0
      form.setValue('total', amount * price)
    }
  }, [orderType, instantPrice, form])

  // Calculate total based on order type and form values
  let total = 0
  if (orderType === 'market') {
    if (toggleValue === 'total') {
      total = form.watch('total') || 0
    } else {
      const amount = form.watch('amount') || 0
      const price = form.watch('price') || instantPrice
      total = amount * price
    }
  } else {
    // For limit and stop-limit, use form total value
    total = form.watch('total') || 0
  }

  if (inputCurrency === 'IRR' && outputCurrency === 'TMN') {
    total = total / 10
  }

  const handleSubmit = async (data: FormSchema<typeof schema>) => {
    const formData: AdvancedTradeBoxFormValues = {
      ...data,
      amount: Number(data.amount),
      total,
      takeProfit,
      stopLoss,
    }
    onSubmit?.(formData)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Col>
          {/* Stop Limit - Activation Price */}
          {orderType === 'stop-limit' && (
            <FormField
              control={form.control}
              name='activationPrice'
              render={({ field }) => (
                <FormItemWrapper inputSize='md'>
                  <AdvancedTradeBoxPriceInput
                    {...field}
                    tab={tab}
                    label={t('order_activation_price')}
                    placeholder={t('order_activation_price_toman')}
                    onPriceChange={(value) => {
                      // Update total when price changes
                      const amount = form.watch('amount') || 0
                      form.setValue('total', amount * value)
                    }}
                  />
                </FormItemWrapper>
              )}
            />
          )}

          {/* Limit and Stop Limit - Price */}
          {(orderType === 'limit' || orderType === 'stop-limit') && (
            <FormField
              control={form.control}
              name='price'
              render={({ field }) => (
                <FormItemWrapper inputSize='md'>
                  <AdvancedTradeBoxPriceInput
                    {...field}
                    tab={tab}
                    label={
                      tab === 'buy'
                        ? t('order_buy_price')
                        : t('order_sell_price')
                    }
                    onPriceChange={(value) => {
                      // Update total when price changes
                      const amount = form.watch('amount') || 0
                      form.setValue('total', amount * value)
                    }}
                  />
                </FormItemWrapper>
              )}
            />
          )}

          {/* Market - Price Display */}
          {orderType === 'market' && (
            <div
              className={twMerge(
                'bg-neutral-surface-5 rounded-xs text-center mb-md',
                isDesktop ? 'p-sm' : 'p-2xs',
              )}
            >
              <Typography size='xs' color='text-neutral-surface-60'>
                {t(
                  isDesktop
                    ? 'order_market_price_description'
                    : 'order_market_price_description_mobile',
                )}
              </Typography>
            </div>
          )}

          {/* Market - Toggle between Total and Amount (Mobile Only) */}
          {orderType === 'market' && !isDesktop && (
            <AdvancedTradeBoxToggle
              value={toggleValue}
              onChange={setToggleValue}
            />
          )}

          {/* Market - Total Input (when toggle is on total) */}
          {orderType === 'market' && toggleValue === 'total' && (
            <FormField
              control={form.control}
              name='total'
              render={({ field }) => (
                <FormItemWrapper inputSize='md'>
                  <Col className={twMerge(isDesktop ? 'gap-md' : '')}>
                    {/* Mobile: Total Input above Balance Slider */}
                    {!isDesktop && (
                      <AdvancedTradeBoxTotalInput
                        {...field}
                        tab={tab}
                        label={t('order_total')}
                      />
                    )}

                    {/* Balance Slider */}
                    <AdvancedTradeBoxBalanceSlider
                      title={
                        tab === 'buy'
                          ? 'order_available_balance_buy'
                          : 'order_available_balance'
                      }
                      value={form.watch('amount') || 0}
                      max={availableAmount}
                      onChange={(value) => {
                        form.setValue('amount', value)
                        // Update total when amount changes
                        const price = form.watch('price') || instantPrice
                        form.setValue('total', value * price)
                      }}
                      currencyLabel={
                        tab === 'buy' ? t('currency_toman') : t('CM2')
                      }
                      onMaxClick={() => push('/wallet/deposit/online-payment')}
                      color='neutral'
                      availableBalance={
                        tab === 'buy'
                          ? inputCurrency === 'IRR' && outputCurrency === 'TMN'
                            ? (+availableBalance / 10).toLocaleString()
                            : availableBalance?.toLocaleString()
                          : availableAmount?.toLocaleString()
                      }
                    />

                    {/* Desktop: Total Input below Balance Slider */}
                    {isDesktop && (
                      <AdvancedTradeBoxTotalInput
                        {...field}
                        tab={tab}
                        label={t('order_total')}
                      />
                    )}
                  </Col>
                </FormItemWrapper>
              )}
            />
          )}

          {/* Market - Amount Input (when toggle is on amount) */}
          {orderType === 'market' && toggleValue === 'amount' && (
            <FormField
              control={form.control}
              name='amount'
              render={({ field }) => (
                <FormItemWrapper inputSize='md'>
                  <Col className={twMerge(isDesktop ? 'gap-md' : '')}>
                    <AdvancedTradeBoxAmountInput
                      {...field}
                      max={availableAmount}
                      tab={tab}
                      assetLabel={t('CM2')}
                      label={
                        tab === 'buy'
                          ? t('order_buy_amount')
                          : t('order_sell_amount')
                      }
                      onAmountChange={(value) => {
                        // Update total when amount changes
                        const price = form.watch('price') || instantPrice
                        form.setValue('total', value * price)
                      }}
                    />

                    {/* Balance Slider */}
                    <AdvancedTradeBoxBalanceSlider
                      title={
                        tab === 'buy'
                          ? 'order_available_balance_buy'
                          : 'order_available_balance'
                      }
                      value={field.value}
                      max={availableAmount}
                      onChange={(value) => {
                        field.onChange(value)
                        // Update total when amount changes
                        const price = form.watch('price') || instantPrice
                        form.setValue('total', value * price)
                      }}
                      currencyLabel={
                        tab === 'buy' ? t('currency_toman') : t('CM2')
                      }
                      onMaxClick={() => push('/wallet/deposit/online-payment')}
                      color='neutral'
                      availableBalance={
                        tab === 'buy'
                          ? inputCurrency === 'IRR' && outputCurrency === 'TMN'
                            ? (+availableBalance / 10).toLocaleString()
                            : availableBalance?.toLocaleString()
                          : availableAmount?.toLocaleString()
                      }
                    />
                  </Col>
                </FormItemWrapper>
              )}
            />
          )}

          {/* Limit and Stop Limit - Amount Input (always shown) */}
          {(orderType === 'limit' || orderType === 'stop-limit') && (
            <FormField
              control={form.control}
              name='amount'
              render={({ field }) => (
                <FormItemWrapper inputSize='md'>
                  <Col className={twMerge(isDesktop ? 'gap-md' : '')}>
                    <AdvancedTradeBoxAmountInput
                      {...field}
                      max={availableAmount}
                      tab={tab}
                      assetLabel={t('CM2')}
                      label={
                        tab === 'buy'
                          ? t('order_buy_amount')
                          : t('order_sell_amount')
                      }
                      onAmountChange={(value) => {
                        // Update total when amount changes
                        const price = form.watch('price') || instantPrice
                        form.setValue('total', value * price)
                      }}
                    />

                    {/* Balance Slider */}
                    <AdvancedTradeBoxBalanceSlider
                      title={
                        tab === 'buy'
                          ? 'order_available_balance_buy'
                          : 'order_available_balance_sell'
                      }
                      value={field.value}
                      max={availableAmount}
                      onChange={(value) => {
                        field.onChange(value)
                        // Update total when amount changes
                        const price = form.watch('price') || instantPrice
                        form.setValue('total', value * price)
                      }}
                      currencyLabel={
                        tab === 'buy' ? t('currency_toman') : t('CM2')
                      }
                      onMaxClick={() => push('/wallet/deposit/online-payment')}
                      color='neutral'
                      availableBalance={
                        tab === 'buy'
                          ? inputCurrency === 'IRR' && outputCurrency === 'TMN'
                            ? (+availableBalance / 10).toLocaleString()
                            : availableBalance?.toLocaleString()
                          : availableAmount?.toLocaleString()
                      }
                    />
                  </Col>
                </FormItemWrapper>
              )}
            />
          )}

          {/* Limit and Stop Limit - Total Input */}
          {(orderType === 'limit' || orderType === 'stop-limit') && (
            <FormField
              control={form.control}
              name='total'
              render={({ field }) => (
                <FormItemWrapper inputSize='md'>
                  <AdvancedTradeBoxTotalInput
                    {...field}
                    tab={tab}
                    label={t('order_total')}
                    onChange={(value) => {
                      field.onChange(value)
                      // Update amount when total changes
                      const price = form.watch('price') || instantPrice
                      if (price > 0) {
                        const newAmount = value / price
                        form.setValue('amount', newAmount)
                      }
                    }}
                  />
                </FormItemWrapper>
              )}
            />
          )}

          {/* Limit - Take Profit / Stop Loss Checkboxes */}
          {orderType === 'limit' && (
            <div className='mb-md'>
              {/* Desktop: Separate checkboxes */}
              {isDesktop && (
                <Row className='gap-lg'>
                  <AdvancedTradeBoxCheckbox
                    checked={takeProfit}
                    onChange={setTakeProfit}
                    label={t('order_take_profit')}
                  />
                  <AdvancedTradeBoxCheckbox
                    checked={stopLoss}
                    onChange={setStopLoss}
                    label={t('order_stop_loss')}
                  />
                </Row>
              )}

              {/* Mobile: Single checkbox for both */}
              {!isDesktop && (
                <AdvancedTradeBoxMobileCheckbox
                  takeProfit={takeProfit}
                  stopLoss={stopLoss}
                  onTakeProfitChange={setTakeProfit}
                  onStopLossChange={setStopLoss}
                />
              )}
            </div>
          )}

          {/* Take Profit / Stop Loss Inputs */}
          {orderType === 'limit' && (takeProfit || stopLoss) && (
            <Col>
              {takeProfit && (
                <FormField
                  control={form.control}
                  name='takeProfit'
                  render={({ field }) => (
                    <FormItemWrapper inputSize='md'>
                      <AdvancedTradeBoxPriceInput
                        {...field}
                        tab={tab}
                        label={t('order_take_profit')}
                        placeholder={t('order_take_profit_toman')}
                      />
                    </FormItemWrapper>
                  )}
                />
              )}

              {stopLoss && (
                <FormField
                  control={form.control}
                  name='stopLoss'
                  render={({ field }) => (
                    <FormItemWrapper inputSize='md'>
                      <AdvancedTradeBoxPriceInput
                        {...field}
                        tab={tab}
                        label={t('order_stop_loss')}
                        placeholder={t('order_stop_loss_toman')}
                      />
                    </FormItemWrapper>
                  )}
                />
              )}
            </Col>
          )}

          {/* Fee Display */}
          <AdvancedTradeBoxFeeDisplay orderType={orderType} />

          <AdvancedTradeBoxSubmitButton
            tab={tab}
            onClick={form.handleSubmit(handleSubmit)}
            isLoading={isLoading}
          />
        </Col>
      </form>
    </Form>
  )
}
